import React, { useState, useEffect } from 'react';
import { 
  X, 
  Sparkles, 
  Check, 
  BookOpen, 
  Trophy, 
  Users, 
  Bell,
  Download,
  HelpCircle,
  Shield
} from 'lucide-react';

const APP_VERSION = '2.0';
const WHATS_NEW_KEY = 'dmu-rimp-whats-new-seen';

const WhatsNewModal = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has seen this version's updates
    const seenVersion = localStorage.getItem(WHATS_NEW_KEY);
    const hasExistingData = localStorage.getItem('dmu-rimp-retreat-start') || 
                           localStorage.getItem('dmu-rimp-retreat-notes');
    
    // Only show to existing users who haven't seen v2.0 updates
    if (hasExistingData && seenVersion !== APP_VERSION) {
      setIsVisible(true);
    } else {
      onClose();
    }
  }, [onClose]);

  const handleDismiss = () => {
    localStorage.setItem(WHATS_NEW_KEY, APP_VERSION);
    setIsVisible(false);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[10002] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[var(--secondary)] to-[var(--secondary)] p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6" />
              <h2 className="font-heading text-lg font-bold">What's New!</h2>
            </div>
            <button onClick={handleDismiss} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/90 text-sm mt-2">
            We've redesigned the app with exciting new features!
          </p>
        </div>

        {/* Your Data is Safe */}
        <div className="bg-green-50 border-b border-green-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-green-800">Your Data is Safe!</h3>
              <p className="text-green-700 text-sm">
                All your notes, progress, and start date are preserved. Your retreat continues exactly where you left off.
              </p>
            </div>
          </div>
        </div>

        {/* New Features */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">New Features</p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-4 h-4 text-rose-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 text-sm">Clearer Guidance</h4>
                <p className="text-xs text-gray-500">New "How It Works" section explains the retreat structure</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 text-sm">Progress Tracking</h4>
                <p className="text-xs text-gray-500">New Progress tab with streaks, milestones & daily challenges</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 text-sm">Resources Hub</h4>
                <p className="text-xs text-gray-500">All spiritual resources, links & PDFs in one place</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 text-sm">Accountability Partner</h4>
                <p className="text-xs text-gray-500">Invite friends to do the retreat with you</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 text-sm">Journal Export</h4>
                <p className="text-xs text-gray-500">Export your reflections to PDF anytime</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-800 text-sm">Prayer Reminders</h4>
                <p className="text-xs text-gray-500">Set daily notifications to stay on track</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 mt-4">
            <p className="text-xs text-amber-800">
              <strong>Tip:</strong> Check out the new <strong>Progress</strong> tab in the bottom navigation to see your journey stats!
            </p>
          </div>
        </div>

        {/* Action */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="w-full py-3 bg-[var(--primary)] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[var(--primary-dark)] transition-colors"
          >
            <Check className="w-5 h-5" />
            Got it, let's continue!
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
